import {
  DOCTOR_QUESTION_MAX,
  MEDICAL_ANSWER_BODY_MAX,
  MEDICAL_NOTICE_PUBLISHED_COPY,
  NOTICE_QUESTION_COPY,
  type NoticeMode,
} from "@care-by-ella/contracts";
import type { AnswerDraft } from "@/lib/medical-question";

// TES-1120 / TES-1147 — the body of a medical notice, shared by "Svara som
// medicinskt besked" (MedicalReviewCases), "Besked till patienten"
// (SendDoctorNotice) and the queue's "Skicka nytt besked", so all three ask
// for the same thing in the same words.
//
// TES-1332 (TES-1331, Emelie 2026-10-13) — exactly two mutually exclusive
// modes, as a radio: "Besked – inget svar behövs" and "Besked med fråga –
// patienten ska svara", which adds the required "Din fråga". The generic
// task and its fields (heading, instruction, mark-as-done, upload) are gone.
// The preview shows what she gets: the push, the Home card and the notice
// under Din vård, with the question and its answer field, or the line that
// she cannot reply.
//
// The mode labels, "Din fråga" and the no-reply line are the decision's
// words; the preview's own headings are proposed copy.
const MODES: Array<{ mode: NoticeMode; label: string }> = [
  { mode: "info", label: NOTICE_QUESTION_COPY.modeInfoLabel },
  { mode: "question", label: NOTICE_QUESTION_COPY.modeQuestionLabel },
];

export function MedicalNoticeDraftFields({
  draft,
  onChange,
  name = "notice-mode",
}: {
  draft: AnswerDraft;
  onChange: (draft: AnswerDraft) => void;
  /** The radio group's name; unique per form on the page. */
  name?: string;
}) {
  return (
    <>
      <fieldset className="flex flex-col gap-xs">
        <legend className="mb-xs">Typ av besked</legend>
        {MODES.map((m) => (
          <label key={m.mode} className="flex items-center gap-xs">
            <input
              type="radio"
              name={name}
              value={m.mode}
              checked={draft.mode === m.mode}
              onChange={() => onChange({ ...draft, mode: m.mode })}
            />
            {m.label}
          </label>
        ))}
      </fieldset>
      <label className="flex flex-col gap-xs">
        <span>Medicinskt besked till patienten</span>
        <textarea
          value={draft.body}
          onChange={(e) => onChange({ ...draft, body: e.target.value })}
          rows={5}
          maxLength={MEDICAL_ANSWER_BODY_MAX}
          className="rounded-md border border-surfaceMuted bg-background p-xs"
        />
      </label>
      {draft.mode === "question" ? (
        <label className="flex flex-col gap-xs">
          <span>{NOTICE_QUESTION_COPY.questionLabel}</span>
          <textarea
            value={draft.question}
            onChange={(e) => onChange({ ...draft, question: e.target.value })}
            rows={3}
            maxLength={DOCTOR_QUESTION_MAX}
            required
            aria-required="true"
            className="rounded-md border border-surfaceMuted bg-background p-xs"
          />
          <span className="text-xs text-textSecondary">
            En enda, tydligt avgränsad fråga. Patienten svarar en gång med text,
            utan chatt och utan filuppladdning. Svaret kommer till din arbetskö
            som ”Svar på läkarens fråga”.
          </span>
        </label>
      ) : (
        <p className="text-xs font-medium" role="note">
          {NOTICE_QUESTION_COPY.cannotReply}
        </p>
      )}
      <NoticePreview draft={draft} />
    </>
  );
}

// What she gets, as she gets it. Read only.
function NoticePreview({ draft }: { draft: AnswerDraft }) {
  const question = draft.mode === "question";
  return (
    <details className="rounded-md border border-surfaceMuted bg-background p-xs text-xs">
      <summary className="cursor-pointer text-textSecondary">
        Förhandsvisning för patienten
      </summary>
      <div className="mt-xs flex flex-col gap-sm">
        <div>
          <p className="text-textSecondary">Notifiering</p>
          <p className="font-medium">{MEDICAL_NOTICE_PUBLISHED_COPY.title}</p>
          <p>{MEDICAL_NOTICE_PUBLISHED_COPY.body}</p>
        </div>
        <div>
          <p className="text-textSecondary">Hemskärmskort</p>
          {question ? (
            <>
              <p className="font-medium">{NOTICE_QUESTION_COPY.homeTitle}</p>
              <p className="underline">{NOTICE_QUESTION_COPY.homeCtaLabel}</p>
            </>
          ) : (
            <>
              <p className="font-medium">
                {MEDICAL_NOTICE_PUBLISHED_COPY.title}
              </p>
              <p>{MEDICAL_NOTICE_PUBLISHED_COPY.body}</p>
              <p className="underline">
                {MEDICAL_NOTICE_PUBLISHED_COPY.ctaLabel}
              </p>
            </>
          )}
        </div>
        <div>
          <p className="text-textSecondary">Under Din vård</p>
          <p className="whitespace-pre-wrap">{draft.body.trim() || "…"}</p>
          {question ? (
            <div className="mt-xs flex flex-col gap-xs">
              <p className="font-medium">{NOTICE_QUESTION_COPY.heading}</p>
              <p className="whitespace-pre-wrap">
                {draft.question.trim() || "…"}
              </p>
              <textarea
                disabled
                rows={2}
                aria-label="Patientens textfält"
                className="rounded-md border border-surfaceMuted bg-surface p-xs"
              />
              <button
                type="button"
                disabled
                className="self-start rounded-md bg-brand px-md py-xs text-textOnBrand opacity-60"
              >
                {NOTICE_QUESTION_COPY.answerCta}
              </button>
            </div>
          ) : (
            <p className="mt-xs text-textSecondary">
              {NOTICE_QUESTION_COPY.cannotReply}
            </p>
          )}
        </div>
      </div>
    </details>
  );
}
